import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Aurora } from '../../components/primitives/Aurora'
import { Card } from '../../components/primitives/Card'
import { IlmuMark } from '../../components/primitives/IlmuMark'
import { PageHead } from '../../components/primitives/PageHead'

/**
 * Story-only preview of the proposed home lobby.
 *
 * Mixes two card kinds in one responsive grid:
 *   - ActionCard  — eyebrow / title / body / arrow; primary gets a
 *                   solid 3px left bar in `--ink`. Arrow is pinned to
 *                   the bottom via flex margin-top: auto so cards on
 *                   the same row align cleanly.
 *   - AiCard      — same skeleton, observational eyebrow (HEADS UP /
 *                   PATTERN / POLICY) prefixed with the ILMU mark
 *                   instead of a sparkle, plus the AIPanel aurora-blob
 *                   background (five blurred radial gradients in brand
 *                   colours + paper veil) so the AI surface reads as
 *                   one visual family across the app.
 *
 * Pure presentation. No router, no auth, no data fetching — render with
 * mock props in Storybook to evaluate per-role treatments. Not wired into
 * production `HomePage.tsx` yet; that wiring follows the spec at
 * `docs/superpowers/specs/2026-05-21-home-ai-panel-design.md` once the
 * design is ratified.
 */

export interface ActionCardModel {
  kind: 'action'
  eyebrow: string
  title: string
  body: string
  /** Display-only — preview renders an `<a>` for affordance, no navigation. */
  href: string
  primary?: boolean
  /** Optional inline metric shown next to the title (e.g. "3 in queue · 1 needs your eye"). */
  metric?: string
}

export interface AiCardModel {
  kind: 'ai'
  /** Stable identifier for dismiss tracking — mirrors API card id. */
  id: string
  /** Observational only: HEADS UP, PATTERN, POLICY. Never verdict words. */
  eyebrow: 'HEADS UP' | 'PATTERN' | 'POLICY'
  title: string
  body: string
  /** Where the CTA drops the user. Display-only here. */
  href: string
  cta: string
  /** Footer receipt — `Generated 8:14 AM` style. Audit-trail signal. */
  generatedAt: string
  /** Optional markdown explanation shown in the "Why this?" popover —
   *  Rule / Threshold / Trigger / Source lines. */
  why?: string
}

export type HomeCardModel = ActionCardModel | AiCardModel

export interface HomeLobbyPreviewProps {
  eyebrow: string
  firstName: string
  subtitle: string
  cards: ReadonlyArray<HomeCardModel>
}

export function HomeLobbyPreview({
  eyebrow,
  firstName,
  subtitle,
  cards,
}: Readonly<HomeLobbyPreviewProps>): JSX.Element {
  return (
    <div style={containerStyle}>
      <PageHead
        eyebrow={eyebrow}
        title={`Welcome back, ${firstName}`}
        subtitle={subtitle}
      />
      <div style={gridStyle}>
        {cards.map((card, idx) => {
          // Staggered fade-up — each card lands ~60 ms after the previous.
          // Uses the design-token `lms-fade-up` keyframe so the global
          // `prefers-reduced-motion` gate suppresses it for free.
          const enter: CSSProperties = {
            // height: 100% preserves the grid row-stretch — the inner
            // <a> + Card both use height: 100% to bottom-align trailing
            // content (arrow / receipt).
            height: '100%',
            animation:
              'lms-fade-up var(--motion-duration-slow) var(--motion-ease-out) both',
            animationDelay: `${idx * 60}ms`,
          }
          return (
            <div key={`${card.kind}-${idx}`} style={enter}>
              <CardSwitch card={card} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface CardSwitchProps {
  card: HomeCardModel
}

function CardSwitch({ card }: Readonly<CardSwitchProps>): JSX.Element {
  if (card.kind === 'action') return <ActionCardView card={card} />
  return <AiCardView card={card} />
}

function ActionCardView({ card }: Readonly<{ card: ActionCardModel }>): JSX.Element {
  const cardStyle: CSSProperties = {
    ...cardFillStyle,
    ...(card.primary ? { borderLeft: '3px solid var(--ink)' } : {}),
  }
  return (
    <a href={card.href} style={cardLinkStyle} aria-label={card.title}>
      <Card interactive padding={20} style={cardStyle}>
        <div style={eyebrowStyle}>{card.eyebrow}</div>
        <div style={titleRowStyle}>
          <div style={titleStyle}>{card.title}</div>
          {card.metric ? <div style={metricStyle}>{card.metric}</div> : null}
        </div>
        <div style={bodyStyle}>{card.body}</div>
        <div style={arrowStyle}>→</div>
      </Card>
    </a>
  )
}

interface AiCardViewProps {
  card: AiCardModel
}

function AiCardView({ card }: Readonly<AiCardViewProps>): JSX.Element {
  // Flip streaming off — Aurora's built-in
  // cubic ease-out decel takes another 3 s to settle the blobs in place
  // (≈5 s end-to-end). Mirrors the AIPanel "streaming ended" pose for a
  // passive insight card: the AI introduces itself, then sits still so it
  // doesn't compete with the rest of the page once noticed.
  const [streaming, setStreaming] = useState(true)
  const [whyShown, setWhyShown] = useState(false)
  const whyLinkRef = useRef<HTMLSpanElement | null>(null)
  const [whyPos, setWhyPos] = useState<{ top: number; left: number } | null>(null)
  useEffect(() => {
    const timer = window.setTimeout(() => setStreaming(false), 0)
    return () => window.clearTimeout(timer)
  }, [])

  // Measure the link's viewport position whenever the tooltip opens — placed
  // ABOVE the link via `transform: translate(-50%, -100%)` for centered anchoring.
  useLayoutEffect(() => {
    if (!whyShown) return undefined
    const update = (): void => {
      const el = whyLinkRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setWhyPos({ top: r.top - 10, left: r.left + r.width / 2 })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [whyShown])

  const hasWhy = typeof card.why === 'string' && card.why.length > 0

  return (
    <a href={card.href} style={cardLinkStyle} aria-label={`${card.eyebrow}: ${card.title}`}>
      <Card interactive padding={0} style={aiCardOuterStyle}>
        {/* Aurora wash + paper veil — five animated brand-colour blobs
            drifting in random phase. Streams for 5 s on mount, then the
            Aurora primitive decelerates (cubic ease-out, 3 s) and parks. */}
        <Aurora streaming={streaming} inset={-32} blur={40} veilColor="rgba(250, 247, 242, 0.55)" />

        {/* Content stack — relative so it paints above the decorative layers.
            No manual dismiss button — cards self-clear when the underlying
            signal goes away (approve the request, MC cluster drops below
            threshold, etc.). Manual dismiss was removed 2026-05-21 because
            an accidental click hid the card for 24h with no undo. */}
        <div style={aiContentStyle}>
          <div style={aiEyebrowStyle}>
            <IlmuMark size={16} aria-hidden="true" />
            <span>{card.eyebrow}</span>
          </div>
          <div style={titleStyle}>{card.title}</div>
          <div style={bodyStyle}>{card.body}</div>
          <div style={aiCtaStyle}>{card.cta} →</div>
          <div style={receiptStyle}>
            Generated {card.generatedAt}
            {hasWhy ? (
              <>
                {' · '}
                <span
                  ref={whyLinkRef}
                  tabIndex={0}
                  role="button"
                  aria-describedby={`why-${card.id}`}
                  style={whyLinkStyle}
                  onMouseEnter={() => setWhyShown(true)}
                  onMouseLeave={() => setWhyShown(false)}
                  onFocus={() => setWhyShown(true)}
                  onBlur={() => setWhyShown(false)}
                  onClick={(e) => {
                    // Stop the surrounding <a> from navigating on touch;
                    // toggle so a tap reveals/hides it on mobile.
                    e.preventDefault()
                    e.stopPropagation()
                    setWhyShown((v) => !v)
                  }}
                >
                  Why this?
                </span>
                {whyShown && whyPos
                  ? createPortal(
                      <div
                        id={`why-${card.id}`}
                        role="tooltip"
                        style={{
                          ...whyTooltipStyle,
                          top: whyPos.top,
                          left: whyPos.left,
                        }}
                        onMouseEnter={() => setWhyShown(true)}
                        onMouseLeave={() => setWhyShown(false)}
                      >
                        {renderWhyLines(card.why!)}
                        <span aria-hidden="true" style={whyTooltipArrowStyle} />
                      </div>,
                      document.body,
                    )
                  : null}
              </>
            ) : null}
          </div>
        </div>
      </Card>
    </a>
  )
}

/** Tiny markdown-ish renderer for the Why popover. Splits on newlines,
 *  bolds anything between `**` markers, renders `\`code\`` in mono.
 *  Avoids a full markdown dep for ~10 lines of structured text. */
function renderWhyLines(markdown: string): JSX.Element[] {
  const lines = markdown.split('\n').filter((l) => l.trim().length > 0)
  return lines.map((line, i) => (
    <div key={i} style={whyLineStyle}>
      {renderInline(line)}
    </div>
  ))
}

function renderInline(line: string): JSX.Element[] {
  const parts: JSX.Element[] = []
  let remaining = line
  let key = 0
  // Sequentially eat **bold** and `code` spans; everything else is text.
  while (remaining.length > 0) {
    const bold = remaining.match(/^\*\*([^*]+)\*\*/)
    const code = remaining.match(/^`([^`]+)`/)
    if (bold) {
      parts.push(
        <strong key={key++} style={{ color: 'var(--ink)' }}>
          {bold[1]}
        </strong>,
      )
      remaining = remaining.slice(bold[0].length)
      continue
    }
    if (code) {
      parts.push(
        <code key={key++} style={{ fontFamily: 'var(--font-mono)', fontSize: '90%' }}>
          {code[1]}
        </code>,
      )
      remaining = remaining.slice(code[0].length)
      continue
    }
    const nextSpecial = remaining.search(/\*\*|`/)
    const chunk = nextSpecial === -1 ? remaining : remaining.slice(0, nextSpecial)
    parts.push(<span key={key++}>{chunk}</span>)
    remaining = nextSpecial === -1 ? '' : remaining.slice(chunk.length)
  }
  return parts
}

const containerStyle: CSSProperties = {
  maxWidth: 1280,
  // Horizontal gutter matches the shared `.page` clamp so home + inner pages
  // line up; 32px on narrow screens up to 96px on wide.
  padding: '32px clamp(32px, 6vw, 96px)',
  margin: '0 auto',
  fontFamily: 'var(--font-sans)',
}

const gridStyle: CSSProperties = {
  display: 'grid',
  gap: 12,
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gridAutoRows: '1fr',
}

const cardLinkStyle: CSSProperties = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  height: '100%',
}

/** Action-card outer — fills the grid row and lets children stack vertically
 *  with `margin-top: auto` on the trailing arrow so cards align bottom-to-bottom. */
const cardFillStyle: CSSProperties = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}

const aiCardOuterStyle: CSSProperties = {
  ...cardFillStyle,
  position: 'relative',
  overflow: 'hidden',
  isolation: 'isolate',
  borderColor: 'var(--ilmu-line)',
}

const aiContentStyle: CSSProperties = {
  position: 'relative',
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
}

const eyebrowStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: 'var(--ink-3)',
  marginBottom: 8,
}

const aiEyebrowStyle: CSSProperties = {
  ...eyebrowStyle,
  color: 'var(--ilmu-fg)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
}

const titleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 6,
}

const titleStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 18,
  fontWeight: 500,
  letterSpacing: '-0.01em',
  color: 'var(--ink)',
  marginBottom: 6,
}

const metricStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  color: 'var(--ink-3)',
  flexShrink: 0,
  whiteSpace: 'nowrap',
}

const bodyStyle: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.55,
  color: 'var(--ink-2)',
}

const arrowStyle: CSSProperties = {
  marginTop: 'auto',
  paddingTop: 14,
  color: 'var(--ink-3)',
  fontSize: 14,
}

const aiCtaStyle: CSSProperties = {
  marginTop: 14,
  fontSize: 13,
  color: 'var(--ilmu-fg)',
  fontWeight: 500,
}

const receiptStyle: CSSProperties = {
  marginTop: 'auto',
  paddingTop: 14,
  borderTop: '1px solid rgba(0, 72, 204, 0.15)',
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  color: 'var(--ink-3)',
}

const whyLinkStyle: CSSProperties = {
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
  textUnderlineOffset: 2,
  cursor: 'help',
  color: 'inherit',
  outline: 'none',
}

/** Portaled tooltip — uses `position: fixed` because we render it into
 *  document.body to escape the card's `overflow: hidden`. The (top, left)
 *  coordinates come from the link's getBoundingClientRect at hover time,
 *  with `transform: translate(-50%, -100%)` centering the tooltip above
 *  the link's midpoint. Recomputed on scroll/resize. */
const whyTooltipStyle: CSSProperties = {
  position: 'fixed',
  transform: 'translate(-50%, -100%)',
  zIndex: 1000,
  minWidth: 240,
  maxWidth: 320,
  padding: '10px 12px',
  background: 'var(--paper-raised)',
  border: '1px solid rgba(0, 72, 204, 0.20)',
  borderRadius: 8,
  boxShadow: '0 6px 18px rgba(80, 56, 28, 0.12), 0 1px 3px rgba(80, 56, 28, 0.06)',
  fontSize: 12,
  lineHeight: 1.5,
  color: 'var(--ink-2)',
  fontFamily: 'var(--font-sans)',
  textTransform: 'none',
  letterSpacing: 0,
  whiteSpace: 'normal',
  pointerEvents: 'auto',
}

/** Downward arrow attached to the bottom-center of the tooltip,
 *  pointing at the underlying "Why this?" link. */
const whyTooltipArrowStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '6px solid transparent',
  borderRight: '6px solid transparent',
  borderTop: '6px solid var(--paper-raised)',
  filter: 'drop-shadow(0 1px 0 rgba(0, 72, 204, 0.20))',
}

const whyLineStyle: CSSProperties = {
  marginBottom: 4,
}

